<script lang="ts">
  import { AssetPaths, type AssetStore } from '../../assets/asset-store';
  import { drawSprite, type Sprite } from '../../assets/atlas';
  import { MINIMAP } from '../../game/constants/hud';
  import type { ControlPointEntity, World } from '../../game/world';

  interface Props {
    world: World;
    assets: AssetStore;
    /** The frame clock. Changing it is what redraws the map. */
    now: number;
    /** Set only for a spectator. Its presence is what makes the map take clicks at all, so a player's shot still passes straight through to the canvas. */
    onseek?: ((x: number, y: number) => void) | undefined;
  }

  const { world, assets, now, onseek }: Props = $props();

  function seek(event: MouseEvent): void {
    if (onseek === undefined) return;
    const target = event.currentTarget;
    if (!(target instanceof HTMLCanvasElement)) return;
    const rect = target.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    onseek(
      ((event.clientX - rect.left) / rect.width) * world.world.maxX,
      ((event.clientY - rect.top) / rect.height) * world.world.maxY,
    );
  }

  /**
   * The minimap stays a canvas, and deliberately.
   *
   * Everything else in the HUD is text and images at fixed positions, which is what DOM is good at.
   * This is a scene: up to a few dozen markers whose positions change every frame. As DOM that is
   * a few dozen elements with a style write each per frame — layout thrash for something with no
   * text, no accessibility surface and no touch targets. One `drawImage` loop is the cheaper and
   * simpler answer, and it keeps the base-orbit maths shared with the world renderer.
   *
   * It is its own canvas rather than part of the main one so it composes with the DOM HUD's stacking
   * order instead of having to be drawn after it.
   */
  let canvas: HTMLCanvasElement | undefined = $state();

  const { position } = MINIMAP;

  function draw(): void {
    if (canvas === undefined) return;
    const ratio = window.devicePixelRatio;
    const width = Math.round(position.width * ratio);
    const height = Math.round(position.height * ratio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const ctx = canvas.getContext('2d');
    if (ctx === null) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, position.width, position.height);

    const { maxX, maxY } = world.world;
    if (maxX <= 0 || maxY <= 0) return;
    const mapX = (x: number): number => (x / maxX) * position.width;
    const mapY = (y: number): number => (y / maxY) * position.height;

    const rotated = (sprite: Sprite, angle: number, x: number, y: number, size: number): void => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      drawSprite(ctx, sprite, -size / 2, -size / 2, size, size);
      ctx.restore();
    };

    for (const team of [0, 1]) {
      const base = world.basePosition(team, now);
      const icon = assets.tintedSprite('baseIcon', team, 0);
      if (icon === null) continue;
      rotated(
        icon,
        -base.angle,
        mapX(base.x),
        mapY(base.y),
        position.width / MINIMAP.baseIconDivisor,
      );
    }

    for (const entity of world.entities.values()) {
      if (entity.kind === 'soccerBall') {
        const ball = assets.soccerBallSprite();
        if (ball !== null) {
          rotated(
            ball,
            0,
            mapX(entity.displayX),
            mapY(entity.displayY),
            position.width / MINIMAP.soccerBallDivisor,
          );
        }
      } else if (entity.kind === 'controlPoint') {
        const point = entity as ControlPointEntity;
        ctx.fillStyle = point.influence < 0 ? '#11F' : point.influence > 0 ? '#F11' : '#444';
        ctx.beginPath();
        ctx.arc(
          mapX(point.displayX),
          mapY(point.displayY),
          position.width / MINIMAP.controlPointDotDivisor,
          0,
          Math.PI * 2,
        );
        ctx.fill();

        const icon = assets.controlPointSprite(point.pointType);
        if (icon !== null) {
          rotated(
            icon,
            0,
            mapX(point.displayX),
            mapY(point.displayY),
            position.width / MINIMAP.controlPointIconDivisor,
          );
        }
      }
    }

    const teamMarker = (x: number, y: number, angle: number, team: number): void => {
      const { size, colors, outline, outlineWidth } = MINIMAP.teamMarker;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.beginPath();
      ctx.moveTo(size * 0.55, 0);
      ctx.lineTo(-size * 0.4, size * 0.38);
      ctx.lineTo(-size * 0.4, -size * 0.38);
      ctx.closePath();
      ctx.fillStyle = colors[team] ?? colors[0];
      ctx.fill();
      ctx.lineWidth = outlineWidth;
      ctx.strokeStyle = outline;
      ctx.stroke();
      ctx.restore();
    };

    // Two sources, and the entity stream wins where it has the ship: it is interpolated per frame,
    // while the feed lands five times a second. Under FFA there are no sides and the feed is empty.
    if (!world.world.ffa) {
      for (const entity of world.entities.values()) {
        if (entity.kind !== 'player' || entity.health <= 0) continue;
        if (entity.id === world.selfId || world.teamPositions.has(entity.id)) continue;
        if (!world.spectating && entity.team !== world.selfTeam) continue;
        teamMarker(mapX(entity.displayX), mapY(entity.displayY), entity.displayAngle, entity.team);
      }
      for (const [id, marker] of world.teamPositions) {
        if (id === world.selfId) continue;
        const live = world.entities.get(id);
        if (live !== undefined && live.kind === 'player') {
          if (live.health > 0) {
            teamMarker(mapX(live.displayX), mapY(live.displayY), live.displayAngle, live.team);
          }
          continue;
        }
        teamMarker(mapX(marker.displayX), mapY(marker.displayY), marker.displayAngle, marker.team);
      }
    }

    if (world.ffaTargetId >= 0) {
      const coin = assets.coinSprite();
      const target =
        world.ffaTargetId === world.selfId
          ? { displayX: world.selfX, displayY: world.selfY }
          : world.entities.get(world.ffaTargetId);
      if (coin !== null && target !== undefined) {
        rotated(
          coin,
          0,
          mapX(target.displayX),
          mapY(target.displayY),
          position.width / MINIMAP.ffaTargetDivisor,
        );
      }
    }

    // Crosshair through the player's own position, then the dot on top of it.
    const selfX = mapX(world.selfX);
    const selfY = mapY(world.selfY);
    ctx.fillStyle = MINIMAP.crosshairColor;
    ctx.fillRect(selfX, 0, 1, position.height);
    ctx.fillRect(0, selfY, position.width, 1);
    ctx.fillStyle = MINIMAP.selfColor;
    ctx.fillRect(selfX - 1, selfY - 1, 3, 3);
  }

  // Redrawn whenever the frame clock moves. `world` is a plain mutable object, so its contents are
  // not reactive and cannot be depended on — `now` is the signal, and it is the honest one: the map
  // is a per-frame render, not a view of a value that changes occasionally.
  $effect(() => {
    void now;
    draw();
  });
</script>

<!-- `Minimap.png` overhangs the map area it frames, so the frame is positioned by its own inset. -->
<div class="minimap">
  <img
    class="minimap__frame"
    src={AssetPaths.ui('Minimap')}
    alt=""
    style:left="{MINIMAP.frameInset.x}px"
    style:top="{MINIMAP.frameInset.y}px"
    style:width="{position.width + MINIMAP.frameInset.width}px"
    style:height="{position.height + MINIMAP.frameInset.height}px"
  />
  <canvas
    bind:this={canvas}
    class="minimap__canvas"
    class:minimap__canvas--seekable={onseek !== undefined}
    style:width="{position.width}px"
    style:height="{position.height}px"
    aria-label="Minimap"
    data-testid="minimap"
    onclick={seek}
  ></canvas>
</div>

<style>
  .minimap {
    position: absolute;
    /* y = UIResH-130 with a 120px map, so 10px clear of the bottom edge. */
    bottom: 10px;
    left: 10px;
  }

  .minimap__frame {
    position: absolute;
    pointer-events: none;
  }

  .minimap__canvas {
    position: relative;
    display: block;
  }

  .minimap__canvas--seekable {
    pointer-events: auto;
    cursor: crosshair;
  }
</style>
